import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import Loader from 'components/ui/loader/Loader'
import * as fromActions from 'store/actions/lessons.action'
import { IMainState } from 'store/states'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { useLesson } from 'contexts'
import { IEvaluationResume } from 'models/evaluation.model'
import { GenerateContent } from './GenerateContent'
import { Wrapper } from './styled'
import { Evaluation } from './evaluation'

interface IContentBlock {
  lessonId: any
  blockInstances: any
  setMandatoriesRef?: (elements: (HTMLElement | null)[]) => void
  canEdit: boolean
  onEvaluation: boolean
  setOnEvaluation: (value: boolean) => void
  evaluation: IEvaluationResume | null
  setEvaluation: (value: IEvaluationResume) => void
  onRefreshLessonContent?: () => void
}

export const ContentBlock: React.FC<IContentBlock> = ({
  lessonId,
  blockInstances,
  setMandatoriesRef,
  canEdit,
  onEvaluation,
  setOnEvaluation,
  evaluation,
  setEvaluation,
  onRefreshLessonContent,
}) => {
  const { currentLessonContent, loading } = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons
  )
  const course = useSelector(({ courses }: IMainState) => courses.course)
  const dispatch = useDispatch()
  const { next } = useLesson()
  const { t } = useTranslation()
  const count = useRef(0)
  const location = useLocation()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const reload = useCallback(() => {
    blockInstances &&
      lessonId &&
      dispatch({
        type: fromActions.LESSONS_GET_LESSON_CONTENT,
        payload: { blockInstances, lessonId },
      })
  }, [blockInstances, dispatch, lessonId])

  useEffect(() => {
    reload()
  }, [reload])

  const refArray = useRef([])

  useLayoutEffect(() => {
    setMandatoriesRef?.(refArray.current)
    return () => {
      setMandatoriesRef?.([])
    }
  }, [setMandatoriesRef])

  const showEvaluation = (evaluation: IEvaluationResume) => {
    if (currentLessonContent.length > 0) {
      setEvaluation(evaluation)
      setOnEvaluation(true)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [onEvaluation])

  const handleAcceptConfirmation = () => {
    setShowConfirmationModal(false)
    setOnEvaluation(true)
  }

  window.onbeforeunload = (e: any) => {
    if (onEvaluation) {
      e.preventDefault()
      e.returnValue = t('lessons.modals.repeatEvaluation.leave')
    }
  }

  const courseId = course.id

  return (
    <>
      {(loading && !onEvaluation) || !courseId ? (
        <Loader color />
      ) : (
        <>
          {onEvaluation ? (
            <Evaluation
              data={evaluation as IEvaluationResume}
              setOnEvaluation={setOnEvaluation}
              onEvaluationUpdated={(newEvaluation: IEvaluationResume) => {
                setEvaluation(newEvaluation)
                reload()
                onRefreshLessonContent?.()
              }}
            />
          ) : (
            <Wrapper>
              <GenerateContent
                lessonContent={currentLessonContent}
                showEvaluation={showEvaluation}
                courseId={courseId}
                {...{
                  refArray,
                  canEdit,
                  count,
                  location,
                  reload,
                  next,
                  lessonId,
                }}
              />
            </Wrapper>
          )}
          <ConfirmationModal
            show={showConfirmationModal}
            title=""
            text={t('lessons.modals.repeatEvaluation.message')}
            acceptButtonText={t('components.modal.buttons.accept')}
            onAccept={handleAcceptConfirmation}
            onClose={() => setShowConfirmationModal(false)}
          />
        </>
      )}
    </>
  )
}
export default ContentBlock
